import React, { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { classifyApiError } from '../utils/api'
import { useMobileAuth } from '../contexts/MobileAuthContext'
import { PERIOD_OPTIONS } from '../utils/statsPeriod'
import { fetchIncidenceSummary, reprocessGroup } from '../utils/incidences'
import ErrorState from '../components/ErrorState'

const PERIOD_LABELS = PERIOD_OPTIONS.reduce((acc, opt) => ({ ...acc, [opt.value]: opt.label }), {})

const GroupSkeleton = () => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 animate-pulse">
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 space-y-2">
        <div className="h-3 w-full bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-3 w-2/3 bg-gray-200 dark:bg-gray-700 rounded" />
      </div>
      <div className="w-8 h-6 rounded-full bg-gray-200 dark:bg-gray-700" />
    </div>
    <div className="h-2 mt-4 bg-gray-100 dark:bg-gray-700/50 rounded-full" />
  </div>
)

// Tap on the message toggles between the 3-line clamp and the full text.
// `progress` is { done, total } while this group is being reprocessed.
const IncidenceCard = ({ group, index, total, progress, disabled, onReprocess }) => {
  const [expanded, setExpanded] = useState(false)
  // % of ALL failed traces of this integration in the period (same as desktop).
  const pct = total > 0 ? (group.count / total) * 100 : 0

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left flex items-start justify-between gap-3"
      >
        <div className="flex items-start gap-2.5 min-w-0">
          <span className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 text-[11px] font-bold text-gray-500 dark:text-gray-400">
            {index + 1}
          </span>
          <span className={`text-[13px] leading-snug text-gray-700 dark:text-gray-200 break-all ${expanded ? '' : 'line-clamp-3'}`}>
            {group.signature}
          </span>
        </div>
        <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[28px] h-[26px] px-2 rounded-full text-xs font-bold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
          {group.count}
        </span>
      </button>
      <div className="mt-3 flex items-center gap-2.5">
        <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-gray-700/70 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-amber-400 to-red-500 transition-[width] duration-500 ease-out"
            style={{ width: `${Math.max(pct, 4)}%` }}
          />
        </div>
        <span className="flex-shrink-0 text-xs font-bold text-gray-700 dark:text-gray-200 tabular-nums w-12 text-right">
          {pct.toFixed(1)}%
        </span>
      </div>
      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 flex justify-end">
        <button
          type="button"
          onClick={() => onReprocess(group)}
          disabled={disabled}
          className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800/40 active:scale-95 disabled:opacity-50 transition"
        >
          <span className={`material-icons ${progress ? 'animate-spin' : ''}`} style={{ fontSize: '15px' }}>
            {progress ? 'sync' : 'replay'}
          </span>
          {progress ? `Reprocessando ${progress.done}/${progress.total}` : 'Reprocessar tudo'}
        </button>
      </div>
    </div>
  )
}

// Bottom sheet asking to confirm a group reprocess - it re-sends every event
// to its destination (webhooks fire again), so it's never done on one tap.
const ConfirmSheet = ({ group, scanned, total, onCancel, onConfirm }) => (
  <>
    <div className="fixed inset-0 z-40 bg-black/40" onClick={onCancel} />
    <div className="fixed inset-x-0 bottom-0 z-50 bg-white dark:bg-gray-900 rounded-t-3xl px-5 pt-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] shadow-2xl">
      <div className="w-10 h-1 rounded-full bg-gray-200 dark:bg-gray-700 mx-auto mb-4" />
      <p className="text-base font-bold text-gray-900 dark:text-white">
        Reprocessar {group.refs.length} {group.refs.length === 1 ? 'evento' : 'eventos'}?
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 line-clamp-2 break-all">{group.signature}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
        Os eventos serão reenviados para processamento.
        {total > scanned && ` Apenas os ${scanned} erros mais recentes (de ${total}) foram carregados.`}
      </p>
      <div className="flex gap-3 mt-5">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-3 rounded-full text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 active:scale-95 transition"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="flex-1 py-3 rounded-full text-sm font-semibold text-white bg-blue-600 active:scale-95 transition"
        >
          Reprocessar
        </button>
      </div>
    </div>
  </>
)

// Reached from the "Incidências" chip of an IntegrationEventCard. The stat
// (name/supplier) comes via location.state; after a reload it's gone and the
// header falls back to a generic title - the list itself only needs the ids.
const IncidencesPage = () => {
  const { integrationId } = useParams()
  const [searchParams] = useSearchParams()
  const period = PERIOD_LABELS[searchParams.get('period')] ? searchParams.get('period') : 'today'
  const stat = useLocation().state?.stat
  const navigate = useNavigate()
  const { companyId } = useMobileAuth()

  const [loading, setLoading] = useState(true)
  const [errorType, setErrorType] = useState(null)
  const [summary, setSummary] = useState({ groups: [], scanned: 0, total: 0 })

  const load = useCallback(async () => {
    if (!companyId || !integrationId) return
    setLoading(true)
    setErrorType(null)
    try {
      setSummary(await fetchIncidenceSummary(companyId, integrationId, period))
    } catch (error) {
      setErrorType(classifyApiError(error))
    } finally {
      setLoading(false)
    }
  }, [companyId, integrationId, period])

  useEffect(() => {
    load()
  }, [load])

  const [confirmGroup, setConfirmGroup] = useState(null)
  // { key, done, total } while a group is being sent - one group at a time.
  const [reprocessing, setReprocessing] = useState(null)
  const [notice, setNotice] = useState(null) // { type: 'success' | 'error', text }

  useEffect(() => {
    if (!notice) return
    const timer = setTimeout(() => setNotice(null), 5000)
    return () => clearTimeout(timer)
  }, [notice])

  const handleConfirmReprocess = async () => {
    const group = confirmGroup
    setConfirmGroup(null)
    const key = group.signature.toLowerCase()
    setReprocessing({ key, done: 0, total: group.refs.length })
    try {
      const { accepted, stoppedEarly } = await reprocessGroup(group.refs, (done, total) =>
        setReprocessing({ key, done, total })
      )
      const plural = accepted === 1 ? 'evento reenfileirado' : 'eventos reenfileirados'
      if (accepted === 0) {
        setNotice({ type: 'error', text: 'Nenhum evento foi aceito para reprocessamento. Verifique se a integração está ativa.' })
      } else if (stoppedEarly) {
        setNotice({ type: 'error', text: `${accepted} ${plural}. O envio foi interrompido porque um lote não foi aceito.` })
      } else {
        setNotice({ type: 'success', text: `${accepted} ${plural} para reprocessamento` })
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erro ao reprocessar eventos'
      setNotice({ type: 'error', text: error.accepted ? `${detail} (${error.accepted} já reenfileirados)` : detail })
    } finally {
      setReprocessing(null)
      // Reprocessed traces drop out of status=failed, so the groups shrink/vanish.
      load()
    }
  }

  const handleBack = () => {
    if (window.history.state?.idx > 0) navigate(-1)
    else navigate('/dashboard', { replace: true })
  }

  const { groups, scanned, total } = summary

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center gap-3 px-4 py-3 safe-top bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-b border-gray-200 dark:border-gray-800">
        <button
          type="button"
          onClick={handleBack}
          title="Voltar"
          className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full text-gray-500 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 active:scale-95 transition"
        >
          <span className="material-icons" style={{ fontSize: '20px' }}>arrow_back</span>
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
            {stat?.integration_name || 'Incidências'}
          </p>
          <div className="flex items-center gap-1.5 mt-0.5">
            {stat?.supplier_name && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border border-purple-100 dark:border-purple-800/40 truncate max-w-[60%]">
                {stat.supplier_name}
              </span>
            )}
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-gray-100 dark:bg-gray-700/60 text-gray-500 dark:text-gray-400 flex-shrink-0">
              <span className="material-icons" style={{ fontSize: '11px' }}>event</span>
              {PERIOD_LABELS[period]}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 pt-4 pb-6">
        {loading && (
          <div className="flex flex-col gap-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Agrupando incidências...</p>
            <GroupSkeleton />
            <GroupSkeleton />
            <GroupSkeleton />
          </div>
        )}

        {!loading && errorType && <ErrorState type={errorType} onRetry={load} />}

        {!loading && !errorType && groups.length === 0 && (
          <div className="flex flex-col items-center justify-center text-center py-16 px-6">
            <span className="material-icons text-emerald-400 dark:text-emerald-500 mb-3" style={{ fontSize: '44px' }}>
              check_circle
            </span>
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">Nenhum erro no período</p>
          </div>
        )}

        {!loading && !errorType && groups.length > 0 && (
          <>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              <span className="font-semibold text-gray-800 dark:text-gray-100">{groups.length}</span>{' '}
              {groups.length === 1 ? 'tipo de erro' : 'tipos de erro'}
              {' · '}
              <span className="font-semibold text-gray-800 dark:text-gray-100">{scanned}</span>{' '}
              {scanned === 1 ? 'ocorrência' : 'ocorrências'}
              {total > scanned && <span className="text-gray-400 dark:text-gray-500"> (de {total})</span>}
            </p>
            <div className="flex flex-col gap-3">
              {groups.map((group, idx) => (
                <IncidenceCard
                  key={group.signature.toLowerCase()}
                  group={group}
                  index={idx}
                  total={total}
                  progress={reprocessing?.key === group.signature.toLowerCase() ? reprocessing : null}
                  disabled={!!reprocessing}
                  onReprocess={setConfirmGroup}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {confirmGroup && (
        <ConfirmSheet
          group={confirmGroup}
          scanned={scanned}
          total={total}
          onCancel={() => setConfirmGroup(null)}
          onConfirm={handleConfirmReprocess}
        />
      )}

      {notice && (
        <div
          onClick={() => setNotice(null)}
          className={`fixed inset-x-4 bottom-[max(1rem,calc(env(safe-area-inset-bottom)+0.5rem))] z-30 flex items-start gap-2 px-4 py-3 rounded-2xl shadow-lg text-sm text-white ${
            notice.type === 'success' ? 'bg-emerald-600' : 'bg-red-600'
          }`}
        >
          <span className="material-icons flex-shrink-0" style={{ fontSize: '18px' }}>
            {notice.type === 'success' ? 'check_circle' : 'error'}
          </span>
          <span>{notice.text}</span>
        </div>
      )}
    </div>
  )
}

export default IncidencesPage
